import { useBreakpoint } from '@workspace/ui/hooks/useBreakpoint';
import { Globe16 } from '@workspace/ui/icons';
import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Link, type LinkProps, useNavigate } from 'react-router-dom';

import type { Order, OrderShort } from '~/api/Order';
import { company, DEFAULT_COMPANIES } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import { ActiveOrderCard } from '~/components/ActiveOrderCard';
import ActiveOrderCard1 from '~/components/ActiveOrderCard/Variant1/ActiveOrderCard';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import { CarouselIcon, CommonCarousel } from '~/components/CommonCarousel';
import Icon from '~/components/Icon/Icon';
import type { IconTypes } from '~/components/Icon/IconTypes';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import LanguageBottomSheet from '~/components/LanguageSelector/LanguageBottomSheet';
import { LoyaltyMenuItem } from '~/components/LoyaltyMenuItem';
import MainSidebar from '~/components/MainSidebar/MainSidebar';
import { ModalType } from '~/components/Modal/interface';
import { PromocodesMenuItem } from '~/components/PromocodesMenuItem';
import { ReferralProgramMenuItem } from '~/components/ReferralProgramMenuItem';
import SkeletonListRows from '~/components/Skeleton/SkeletonList';
import { useDateLang } from '~/hooks/useDateLang';
import { useModal } from '~/hooks/useModal';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';
import { useRewardedPromocodesStatus } from '~/hooks/useRewardedPromocodesStatus';
import { orderStore, userStore } from '~/stores';
import { getTimeAgo } from '~/utils/getTimeAgo';
import { normalizeOrderId } from '~/utils/normalizeOrderId';
import MobileHeaderAddress from '../Main/MobileHeader/MobileHeaderAddress';
import MobileHeaderLayout from '../Main/MobileHeader/MobileHeaderLayout';
import { ROUTES } from '../Root/constants';
import styles from './Cabinet.module.scss';

import { ActiveOrdersSection } from './components/ActiveOrdersSection';
import { NoOrders } from './components/NoOrders';
import {
  ACTIVE_ORDER_STATUS_CRITERIA,
  MAX_LAST_ORDER_LENGTH,
} from './constants';

export const carouselProps = (company: CompanyName) =>
  company === CompanyName.CityDrinks ? {} : { pagination: { enabled: true } };

const SidebarChevron = () => (
  <Icon type="chevron" size={16} className="icon__rotate-270" />
);

interface SidebarLinkProps extends Omit<LinkProps, 'chidlren'> {
  icon: IconTypes | React.ReactNode;
  label: string;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({
  to,
  icon,
  label,
  state,
}) => (
  <Link to={to} state={state} className="sidebar-section-item">
    {typeof icon === 'string' ? (
      <Icon type={icon as IconTypes} size={24} />
    ) : (
      icon
    )}
    <p className="sidebar-section-item__text">{label}</p>
    {!company.is('mcosmetic') && <SidebarChevron />}
  </Link>
);

const Cabinet = observer(() => {
  const { t } = useTranslation();

  const [orderHistoryApi, setOrderHistoryApi] = useState<
    CarouselApi | undefined
  >(undefined);

  const { onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(orderHistoryApi);

  const [isLangSheetOpen, setIsLangSheetOpen] = useState(false);

  const { openModal } = useModal();
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();

  const handleDeliveryAddress = () => openModal(ModalType.Logout);

  const { orderList } = orderStore;

  const lastOrders =
    orderList
      ?.filter(({ public_status }) => public_status === 'delivered')
      .slice(0, MAX_LAST_ORDER_LENGTH) ?? [];

  const activeOrders =
    orderList?.filter((item) =>
      ACTIVE_ORDER_STATUS_CRITERIA.includes(item.public_status),
    ) ?? [];

  useEffect(() => {
    orderStore.setOrdersPollingEnabled(true);
  }, []);

  useRewardedPromocodesStatus();

  const currentProps = carouselProps(company.name);

  const activeOrdersCarousel = () => {
    if (DEFAULT_COMPANIES.includes(company.name)) {
      return (
        <CommonCarousel
          {...currentProps}
          slideClassName={styles.commonSlide}
          sliderClassName={styles.activeOrdersSlider}
          slidesPerViewProp={isMobile ? 1 : 2}
        >
          {activeOrders.map((order) => (
            <ActiveOrderCard1 key={order.id} order={order} />
          ))}
        </CommonCarousel>
      );
    } else {
      return (
        <Carousel
          opts={{
            align: 'start' as const,
            slidesToScroll: 1,
            containScroll: 'trimSnaps' as const,
          }}
          className={styles.activeOrdersSlider}
        >
          <CarouselContent className={styles.activeOrdersSliderContent}>
            {activeOrders.map((order) => (
              <CarouselItem className={styles.slide} key={order.id}>
                <ActiveOrderCard key={order.id} order={order} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      );
    }
  };

  useEffect(() => {
    if (company.is('mcosmetic') && !isMobile) {
      navigate('/cabinet/personal-data');
    }
  }, []);

  return (
    <div className={styles.contentWr} data-company={company.name}>
      <MobileHeaderLayout
        content={<MobileHeaderAddress />}
        showPersonalData={company.showPersonalDataButtonMobileCabinetPage}
      />
      <div className="content cabinet content-layout">
        <aside className="sidebar">
          <MainSidebar />
        </aside>

        <main className="container">
          <section className="header">
            <div className="title">{t('cabinet')}</div>
          </section>

          {!orderStore.isLoading ? (
            company.match({
              mcosmetic: (
                <ActiveOrdersSection
                  activeOrders={activeOrders}
                  name={userStore.fullName}
                  phone={`+${userStore.phone}`}
                />
              ),
              default:
                activeOrders.length > 0 ? (
                  <>
                    <section className="active-orders list">
                      <div className="title">{t('activeOrders')}</div>
                      {activeOrders.map((order) => (
                        <ActiveOrderCard key={order.id} order={order} />
                      ))}
                    </section>
                    <section className="active-orders slider">
                      <div className="title">{t('activeOrders')}</div>
                      {activeOrdersCarousel()}
                    </section>
                  </>
                ) : (
                  <NoOrders className={styles.noOrders} />
                ),
            })
          ) : (
            <div className="skeleton-container">
              <Skeleton className="title" />
              <SkeletonListRows rows={10}>
                <Skeleton className="skeleton-container__item" />
              </SkeletonListRows>
            </div>
          )}
          {lastOrders.length > 0 && (
            <section className="last-orders">
              <div className="title">{t('lastOrders')}</div>
              <div className="slider2">
                <CarouselIcon
                  onClick={onPrevButtonClick}
                  position="left"
                  variant="white"
                />

                <div className="slider2-items">
                  <Carousel
                    setApi={setOrderHistoryApi}
                    opts={{
                      align: 'start' as const,
                      slidesToScroll: 1,
                      containScroll: 'trimSnaps' as const,
                    }}
                    dir={userStore.dir}
                    key={userStore.dir}
                  >
                    <CarouselContent>
                      {lastOrders.map((order) => (
                        <CarouselItem
                          className={styles.inactiveSlide}
                          key={order.id}
                        >
                          <Link to={`/order/${order.id}`}>
                            <OrderItem order={order} />
                          </Link>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>
                </div>

                <CarouselIcon
                  onClick={onNextButtonClick}
                  position="right"
                  variant="white"
                />
              </div>
            </section>
          )}

          <div
            className={cn(
              'sidebar-section-container mobile',
              company.match({
                mcosmetic:
                  'relative z-1 before:absolute before:-top-8 before:-left-4 before:-z-1 before:block before:h-8 before:w-screen before:rounded-t-6.5 before:bg-bg-base max-md:before:content-[""]',
              }),
            )}
          >
            <section
              className={cn(
                'sidebar-section',
                company.match({
                  mcosmetic: '-mt-4',
                }),
              )}
            >
              <SidebarLink
                to="/cabinet/personal-data"
                icon="filter"
                label={company.match({
                  citydrinks: t('cabinet'),
                  default: t('personalData'),
                })}
              />
              {company.match({
                citydrinks: (
                  <SidebarLink
                    to={ROUTES.favs}
                    icon="fav"
                    label={t('savedAndPurchased')}
                  />
                ),
                default: null,
              })}
              <SidebarLink
                to="/cabinet/orders"
                icon="orders"
                label={t('orders')}
              />
              <SidebarLink
                to="/cabinet/address"
                icon={company.match({
                  circlek: 'location-fill-pin',
                  default: 'location',
                })}
                label={t('deliveryAddress')}
              />
              <SidebarLink
                to="/cabinet/payment-method"
                icon="cards"
                label={t('paymentMethod')}
              />
              {company.is('mcosmetic') && (
                <button
                  type="button"
                  className="sidebar-section-item cursor-pointer border-none bg-transparent"
                  onClick={() => setIsLangSheetOpen(true)}
                >
                  <Globe16 width={24} height={24} />
                  <p className="sidebar-section-item__text">{t('language')}</p>
                </button>
              )}
            </section>
            <section className="sidebar-section">
              {company.match({
                citydrinks: (
                  <PromocodesMenuItem
                    className="sidebar-section-item"
                    labelClassName="sidebar-section-item__text"
                  >
                    <SidebarChevron />
                  </PromocodesMenuItem>
                ),
                default: (
                  <ReferralProgramMenuItem
                    className="sidebar-section-item"
                    labelClassName="sidebar-section-item__text"
                  >
                    {!company.is('mcosmetic') && <SidebarChevron />}
                  </ReferralProgramMenuItem>
                ),
              })}
              <LoyaltyMenuItem
                className="sidebar-section-item"
                labelClassName="sidebar-section-item__text"
              >
                <SidebarChevron />
              </LoyaltyMenuItem>
              {!company.hideAboutUs && (
                <SidebarLink to="/our-story" icon="link" label={t('aboutUs')} />
              )}
              {company.match({
                okie: (
                  <>
                    <SidebarLink to="/about" icon="info" label={t('about')} />
                    <SidebarLink to="/faq" icon="message" label={t('faq')} />
                    <SidebarLink
                      to="/customer-care"
                      icon="phone"
                      label={t('customerCare')}
                    />
                    <SidebarLink
                      to="/payment"
                      icon="cash"
                      label={t('payment')}
                    />
                  </>
                ),
              })}
              {company.match({
                circlek: (
                  <SidebarLink
                    to={`tel:${company.config.phoneNumber}`}
                    icon="message"
                    label={t('contactUs')}
                  />
                ),
                default: !company.hideAboutUs && (
                  <SidebarLink
                    to="/contact-us"
                    icon="edit"
                    label={t('contactUs')}
                  />
                ),
              })}
              <SidebarLink
                to="/legals"
                icon="document"
                label={t(
                  company.isDefaultCompany ? 'legals' : 'termsAndConditions',
                )}
                state={{ source: 'cabinet' }}
              />
            </section>
            <section className="sidebar-section">
              <button
                className="button no-padding item sidebar-section__logout"
                onClick={handleDeliveryAddress}
              >
                <Icon type="logout" size={24} />
                {t('logOut')}
              </button>
            </section>
          </div>
        </main>
      </div>

      <LanguageBottomSheet
        isOpen={isLangSheetOpen}
        onClose={() => setIsLangSheetOpen(false)}
      />
    </div>
  );
});

const OrderItem = observer(({ order }: { order: Order | OrderShort }) => {
  const { locale } = useDateLang();

  const productItems = order.items.filter((i) => !i.bundle_id);
  const bundleItems = order.bundles ?? [];
  const len = productItems.length + bundleItems.length - 4;
  const timeAgo = getTimeAgo(new Date(order.sla.deliver_by_max), locale);

  return (
    <div className="item">
      <div className="number-wrapper">
        <div className="number">#{normalizeOrderId(order.order_id)}</div>
        <div className="ago">{timeAgo}</div>
      </div>
      <div className="buttle-wrapper">
        {[...bundleItems, ...productItems].map((item) => (
          <div className="buttle" key={item.id}>
            <CloudflareResponseImage
              src={item.image ?? ''}
              srcParams={{ width: 48, height: 48 }}
              srcSetParams={defaultSrcSetParams}
              alt={item.name}
              loading="lazy"
            />
          </div>
        ))}
        {len > 0 && (
          <div className="buttle">
            <div className="count">+{len}</div>
          </div>
        )}
      </div>
    </div>
  );
});

export default Cabinet;
